import type { TabularSheetCellValue } from '../../shared/tabular-sheet-payload.model';

function isPrimitive(value: unknown): value is string | number | boolean {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/** Excel headers match C# DTO names: `arrivalDate` → `ArrivalDate`. Numeric array indexes stay digits. */
function toExcelColumnSegment(key: string): string {
    if (key === '' || /^\d+$/.test(key)) {
        return key;
    }
    return key.charAt(0).toUpperCase() + key.slice(1);
}

function joinKey(prefix: string, key: string): string {
    const segment = toExcelColumnSegment(key);
    return prefix === '' ? segment : `${prefix}.${segment}`;
}

function primitiveArrayToCell(values: unknown[]): string {
    return values.map((v) => (v == null ? '' : String(v))).join(', ');
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * BE often wraps the Trace payload as `{ payload: { ArrivalDate, ... } }`.
 * Excel headers should be `ArrivalDate`, not `payload.ArrivalDate`.
 */
function unwrapTracePayloadObject(value: unknown): unknown {
    if (!isPlainObject(value)) {
        return value;
    }
    const inner = value['payload'] ?? value['Payload'];
    if (!isPlainObject(inner)) {
        return value;
    }
    const rest: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value)) {
        if (key === 'payload' || key === 'Payload') {
            continue;
        }
        rest[key] = nested;
    }
    return { ...inner, ...rest };
}

/**
 * Flattens a DWT payload JSON object into Excel columns.
 * Keys are PascalCase (`arrivalDate` → `ArrivalDate`); nested keys use dotted paths (`Carrier.Name`).
 */
export function flattenJsonToColumns(
    value: unknown,
    prefix = ''
): Record<string, TabularSheetCellValue> {
    if (value === null || value === undefined) {
        return prefix === '' ? {} : { [prefix]: '' };
    }

    if (prefix === '' && typeof value === 'string') {
        try {
            return flattenJsonToColumns(JSON.parse(value));
        } catch {
            return {};
        }
    }

    const source = prefix === '' ? unwrapTracePayloadObject(value) : value;

    if (isPrimitive(source)) {
        return prefix === '' ? {} : { [prefix]: source };
    }

    if (Array.isArray(source)) {
        if (
            source.every(
                (item) => item === null || item === undefined || isPrimitive(item)
            )
        ) {
            return prefix === '' ? {} : { [prefix]: primitiveArrayToCell(source) };
        }
        const fromArray: Record<string, TabularSheetCellValue> = {};
        source.forEach((item, index) => {
            Object.assign(fromArray, flattenJsonToColumns(item, joinKey(prefix, String(index))));
        });
        return fromArray;
    }

    if (typeof source === 'object') {
        const fromObject: Record<string, TabularSheetCellValue> = {};
        for (const [key, nested] of Object.entries(source as Record<string, unknown>)) {
            Object.assign(fromObject, flattenJsonToColumns(nested, joinKey(prefix, key)));
        }
        return fromObject;
    }

    return prefix === '' ? {} : { [prefix]: String(source) };
}
